import {match} from 'ts-pattern';

import {any} from 'ramda';

import {Nullish} from 'shared/web';

import {BankAccount, getDefaultBankAccount} from './getDefaultBankAccount';

/**
 * @internal
 * Mid-level utility for validating origin account.
 * Components should use `getDefaultBankAccountFromBranch` instead.
 *
 * Validation priority:
 * 1. If originBankAccount exists in availableBankAccounts → returns it
 * 2. If not found, falls back to primary account (isPrimary === true)
 * 3. If no primary and selectFirstIfNoPrimary=true (default), falls back to first account  ← DEFAULT
 * 4. If no primary and selectFirstIfNoPrimary=false, returns null      ← CONDITIONAL
 * 5. If no accounts available → returns null
 *
 * Note: Comparison is case-sensitive and exact (no trimming or normalization).
 *
 * @param originBankAccount - The bank account number to validate (typically from a document)
 * @param availableBankAccounts - Array of currently available bank accounts for the branch
 *  @param selectFirstIfNoPrimary - If true, selects the first account if no primary is found (default: true)
 * @returns The validated account number (string) or null if no valid account found
 *
 * @example
 * // Origin account exists in available accounts
 * const accounts = [
 *   { accountNumber: '123', isPrimary: false },
 *   { accountNumber: '456', isPrimary: true }
 * ];
 * getValidatedBankAccount('123', accounts);
 * // Returns: '123' (origin is valid)
 *
 * @example
 * // No origin, no primary, returns null (false for selectFirstIfNoPrimary)
 * const noPrimary = [
 *   { accountNumber: '111', isPrimary: false },
 *   { accountNumber: '222', isPrimary: false }
 * ];
 * getValidatedBankAccount(null, noPrimary, false);
 * // Returns: null
 *
 * @example
 * // No origin, no primary, uses default behavior (falls back to first account)
 * getValidatedBankAccount(null, noPrimary);
 * // Returns: '111' (first account with selectFirstIfNoPrimary=true)
 *
 * @example
 * // No accounts available
 * getValidatedBankAccount('123', []);
 * // Returns: null
 */

export const getValidatedBankAccount = (
  originBankAccount: string | Nullish,
  availableBankAccounts: BankAccount[] | Nullish,
  selectFirstIfNoPrimary = true
) => {
  const bankAccounts = availableBankAccounts ?? [];
  const hasOrigin =
    Boolean(originBankAccount) &&
    any((account: BankAccount) => account.accountNumber === originBankAccount, bankAccounts);

  return match(hasOrigin)
    .with(true, () => originBankAccount ?? null)
    .otherwise(() => {
      const defaultBankAccount = getDefaultBankAccount(
        availableBankAccounts,
        selectFirstIfNoPrimary
      );
      return defaultBankAccount ? defaultBankAccount.accountNumber : null;
    });
};
